import api from './api';
import type { OutreachOutcomeValue } from '../constants/outreachConstants';
import type { TaskDepartment } from '../constants/taskDepartments';

// ---- Task Type Constants (mirrors backend enum) ----

export const TASK_TYPES = {
  patient_counselling: 'Patient Counselling',
  reach_out_to_psp: 'Reach out to PSP',
  reach_out_to_patient: 'Reach out to Patient',
  reach_out_to_md: 'Reach out to MDs Office',
  reach_out_to_insurance: 'Reach out to Insurance',
  reach_out_to_ext_pharmacy: 'Reach out to Ext. Pharmacy for Transfer In',
  transfer_out: 'Transfer Out',
  billing_troubleshooting: 'Billing Troubleshooting',
  // PRX-66. `MANUALLY_CREATABLE_TASK_TYPES` is `Object.values(TaskType)`, so
  // the type is creatable the day it is added and this dropdown must offer it.
  // It therefore appears at every pharmacy, GreenShield included — accepted
  // rather than filtered, because narrowing an exhaustive-by-construction list
  // is the drift its own comment warns about.
  blister_pack_change: 'Blister Pack Change',
} as const;

export type TaskTypeKey = keyof typeof TASK_TYPES;

// ---- Types ----

export interface TaskQueueItem {
  id: string;
  type: 'troubleshooting_billing' | 'troubleshooting_order' | 'troubleshooting_nexi' | 'manual' | 'counseling';
  title: string;
  description?: string;
  priority: string;
  status: string;
  assignedTo?: string;
  dueDate?: string;
  dueTime?: string;
  patient?: { name: string; id?: string; phone?: string };
  source: string;
  orderNumber?: string;
  billingItemId?: string;
  orderId?: string;
  createdAt: string;
  activityLog?: Array<{ timestamp: string; user: string; action: string; note?: string }>;
  stage?: string;
  medication?: string;
  deliveryDate?: string;
  deliveryMethod?: string;
  deliveryType?: string;
  timeInTroubleshooting?: number;
  metadata?: any;
  // Task Queue wireframe fields
  entryMethod?: string;
  troubleshootingReason?: string;
  taskType?: string;
  patientDob?: string;
  krollId?: string;
  foxId?: string;
  linkStatus?: string;
  rxNumber?: string;
  counselingOrigin?: string;
  assignedToName?: string;
  /** PRX-67 — set from the task type at creation (R2), editable by hand (D1). */
  department?: TaskDepartment | null;
  /** PRX-132 — the Communication Center thread this task was made from (opens via /communications?thread=). */
  sourceThreadId?: string | null;
  /** GP-1114: the linked counseling record's real status / declined-reason, so
   *  the queue can reliably distinguish a DECLINED counseling from a completed
   *  one instead of collapsing both into "Complete". */
  counselingStatus?: string;
  counselingDeclinedReason?: string;
  /** GP-1203: Rx number of the rebuilt prescription whose completed counseling
   *  satisfied this task. Set together with counselingStatus='superseded'. */
  supersededByRxNumber?: string;
  /** GP-1365: how many reach-out attempts have been logged against this task
   *  (derived server-side from the activity log). Drives the "attempt N" copy
   *  and the escalation nudge in the outreach dialog. */
  outreachAttempts?: number;
}

export interface TaskQueueResponse {
  success: boolean;
  items: TaskQueueItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  stats: {
    total: number;
    /** Mutually-exclusive with `overdue` (excludes overdue + terminal). Prefer this
     *  over summing byStatus for the "In Progress" tile (B3 / GP-925). */
    inProgress: number;
    byStatus: Record<string, number>;
    byPriority: Record<string, number>;
    byType: { manual: number; troubleshooting: number };
    overdue: number;
  };
  timestamp: string;
}

/**
 * R7 — response of `GET task-queue/:pharmacyId/counts`. Open tasks only
 * (status in open/in_progress/on_hold), independent of the current list
 * filters. Backs both the queue's tabs (`useTaskQueueCounts`) and the sidebar
 * badge (Task 3).
 */
export interface TaskQueueCounts {
  all: number;
  byDepartment: Record<TaskDepartment, number> & { none: number };
  mine: number;
}

export interface CreateManualTaskDto {
  title: string;
  description?: string;
  priority?: 'urgent' | 'high' | 'medium' | 'low';
  assignedTo?: string;
  dueDate?: string;
  dueTime?: string;
  patientId?: string;
  patientName?: string;
  patientPhone?: string;
  billingItemId?: string;
  orderId?: string;
  orderNumber?: string;
  source?: string;
  category?: string;
  tags?: string[];
  // Task Queue wireframe fields
  entryMethod?: string;
  troubleshootingReason?: string;
  taskType?: string;
  patientDob?: string;
  krollId?: string;
  foxId?: string;
  linkStatus?: string;
  rxNumber?: string;
  /** PRX-67 — defaults from `taskType` via `departmentForTaskType` when omitted (D1). */
  department?: TaskDepartment | null;
}

export interface UpdateManualTaskDto {
  title?: string;
  /**
   * GP-1464 — a resolution-pathway step just ticked done, by its exact action
   * text. Recorded on the task's activity log, which is where step progress is
   * read back from.
   */
  stepCompleted?: string;
  description?: string;
  priority?: 'urgent' | 'high' | 'medium' | 'low';
  status?: 'open' | 'in_progress' | 'on_hold' | 'completed';
  assignedTo?: string;
  dueDate?: string;
  dueTime?: string;
  patientId?: string;
  patientName?: string;
  patientPhone?: string;
  billingItemId?: string;
  orderId?: string;
  orderNumber?: string;
  category?: string;
  tags?: string[];
  // Task Queue wireframe fields
  entryMethod?: string;
  troubleshootingReason?: string;
  taskType?: string;
  patientDob?: string;
  krollId?: string;
  foxId?: string;
  linkStatus?: string;
  rxNumber?: string;
  /** PRX-67 — editable by hand (D1); `null` clears it back to uncategorized. */
  department?: TaskDepartment | null;
}

/** GP-1365 — body of POST /api/task-queue/:pharmacyId/items/:taskId/outreach */
export interface LogOutreachAttemptDto {
  outcome: OutreachOutcomeValue;
  /** Plain calendar date (YYYY-MM-DD) — no timestamps, no timezone conversion. */
  dueDate: string;
  note?: string;
}

export interface TaskQueueFilters {
  status?: string;
  priority?: string;
  assignedTo?: string;
  source?: string;
  category?: string;
  searchTerm?: string;
  type?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  // Wireframe filters
  linkStatus?: string;
  taskType?: string;
  dueDateFilter?: string;
  /**
   * PRX-67 — one department tab (R3). `assignedTo: 'me'` is the Mine tab
   * (resolved server-side to the caller's identifier, R8/R3) and already flows
   * through the existing `assignedTo` param below.
   */
  department?: string;
  // Date range filters (YYYY-MM-DD inclusive)
  createdDateFrom?: string;
  createdDateTo?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
}

// ---- API Functions ----

export async function getTaskQueue(
  pharmacyId: string,
  filters: TaskQueueFilters = {},
): Promise<TaskQueueResponse> {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.priority) params.set('priority', filters.priority);
  if (filters.assignedTo) params.set('assignedTo', filters.assignedTo);
  if (filters.source) params.set('source', filters.source);
  if (filters.category) params.set('category', filters.category);
  if (filters.searchTerm) params.set('searchTerm', filters.searchTerm);
  if (filters.type) params.set('type', filters.type);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.sortBy) params.set('sortBy', filters.sortBy);
  if (filters.sortOrder) params.set('sortOrder', filters.sortOrder);
  if (filters.linkStatus) params.set('linkStatus', filters.linkStatus);
  if (filters.taskType) params.set('taskType', filters.taskType);
  if (filters.dueDateFilter) params.set('dueDateFilter', filters.dueDateFilter);
  if (filters.department) params.set('department', filters.department);
  if (filters.createdDateFrom) params.set('createdDateFrom', filters.createdDateFrom);
  if (filters.createdDateTo) params.set('createdDateTo', filters.createdDateTo);
  if (filters.dueDateFrom) params.set('dueDateFrom', filters.dueDateFrom);
  if (filters.dueDateTo) params.set('dueDateTo', filters.dueDateTo);

  const query = params.toString();
  const url = `/api/task-queue/${pharmacyId}${query ? `?${query}` : ''}`;
  const response = await api.get(url);
  return response.data;
}

/**
 * R7 — open-task counts (all / per department / mine), independent of the
 * queue's current list filters. Feeds the tab strip's chips
 * (`useTaskQueueCounts`) and the sidebar's Task Queue badge (Task 3).
 */
export async function getTaskQueueCounts(pharmacyId: string): Promise<TaskQueueCounts> {
  const response = await api.get(`/api/task-queue/${pharmacyId}/counts`);
  return response.data;
}

/**
 * GP-1337 — assign one or many tasks to a pharmacist.
 *
 * Same shape as the Billing Queue's bulk-assign, so the inline row picker (one
 * id) and the bulk action bar (many) share one call. `assignedTo: null`
 * unassigns. The backend validates that the assignee actually holds a role that
 * can conduct counseling and rejects the write otherwise — callers must surface
 * the error rather than assume success.
 */
export async function bulkAssignTasks(
  pharmacyId: string,
  itemIds: string[],
  assignedTo: string | null,
): Promise<{ success: boolean; message: string; updatedCount: number }> {
  const response = await api.post(`/api/task-queue/${pharmacyId}/bulk-assign`, {
    itemIds,
    assignedTo,
  });
  return response.data;
}

/**
 * GP-1365 — record a reach-out attempt and defer the task in one call.
 *
 * `dueDate` MUST be a plain `YYYY-MM-DD` string (never `.toISOString()`) — it
 * lands directly in the task's `due_date` date column. The server rejects a date
 * in the past or one after the linked delivery date, so callers must surface the
 * error instead of assuming the save landed.
 */
export async function logOutreachAttempt(
  pharmacyId: string,
  taskId: string,
  dto: LogOutreachAttemptDto,
): Promise<{ success: boolean; item: TaskQueueItem }> {
  const response = await api.post(
    `/api/task-queue/${pharmacyId}/items/${taskId}/outreach`,
    dto,
  );
  return response.data;
}

export async function createManualTask(
  pharmacyId: string,
  dto: CreateManualTaskDto,
): Promise<{ success: boolean; item: TaskQueueItem }> {
  const response = await api.post(`/api/manual-tasks/${pharmacyId}`, dto);
  return response.data;
}

export async function updateManualTask(
  id: string,
  dto: UpdateManualTaskDto,
): Promise<{ success: boolean; item: TaskQueueItem }> {
  const response = await api.patch(`/api/manual-tasks/${id}`, dto);
  return response.data;
}

export async function addTaskNote(
  id: string,
  note: string,
): Promise<{ success: boolean; item: TaskQueueItem }> {
  const response = await api.post(`/api/manual-tasks/${id}/note`, { note });
  return response.data;
}

export async function deleteManualTask(
  id: string,
): Promise<{ success: boolean; message: string }> {
  const response = await api.delete(`/api/manual-tasks/${id}`);
  return response.data;
}

export async function getManualTaskDetail(
  pharmacyId: string,
  id: string,
): Promise<{ success: boolean; item: TaskQueueItem }> {
  const response = await api.get(`/api/manual-tasks/${pharmacyId}/detail/${id}`);
  return response.data;
}
